'use no memo';
/*

  Design Specification: converting this to React from Coldfusion
  https://app.kalosflorida.com/index.cfm?action=admin:contracts.edit&contract_id=3365&p=1

*/
import { ActivityLog, Contract, Event, EventList, Invoice, Property } from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  DataTable,
  DataTableColumnHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import AddCircleOutlineTwoTone from '@mui/icons-material/AddCircleOutlineTwoTone';
import EditSharp from '@mui/icons-material/EditSharp';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import { default as IconButton } from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { type FC, useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { useAuth } from '../../../context/AuthContext';
import { queryKeys } from '../../../hooks/react-query/constants';
import {
  useEventBatchGetQuery,
  useEventCreateMutation,
} from '../../../hooks/react-query/useEventsQuery';
import {
  ActivityLogClientService,
  debounce,
  EventClientService,
  formatDate,
} from '../../../tools/helpers';
import {
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
} from '../../CustomerDetails/components/constants';
import { Loader } from '../../Loader/main';
import { Alert } from '../Alert';
import { Confirm } from '../Confirm';
import { EditInvoiceData } from '../EditInvoiceData';
import { type Schema } from '../Form';
import { Modal } from '../Modal';
import { type Options, PlainForm } from '../PlainForm';
import { SectionBar } from '../SectionBar';
import { ServiceCall } from '../ServiceCall';
import { ServiceRequest } from '../ServiceCall/requestIndex';
import {
  CANCEL_LABEL,
  CONFIRM_CLOSE_WITHOUT_SAVE_DIALOG,
  CONFIRM_SAVE_DIALOG,
  CONFIRM_SAVE_LABEL,
  ERROR_LABEL,
  INVOICE_SECTION_NAME,
  SAVE_LABEL,
  SERVICE_CALL_SUBTITLE,
  WARNING_LABEL,
} from './constants';
import { ACTIONS, type ContractErrors, reducer, type State } from './reducer';
import {
  applyContractFormChange,
  buildContractActivityMessage,
  buildContractPmEvent,
  buildNewContract,
  checkContractChange,
  checkContractErrors,
  filterPropertiesByAddress,
  GROUP_BILLING_OPTIONS,
  isPropertyOnContract,
  orderContractProperties,
  saveContractInvoice,
  saveContractRecord,
  toggleSelectedProperty,
  useEditContractQueries,
} from './useEditContract';

export interface Output {
  contractData: Contract;
  propertiesSelected: Property[];
  invoiceData: Invoice;
}
interface props {
  userID: number;
  contractID: number;
  onSaveFinished?: (savedContract: Output) => any; // Used to Close Modal
  onClose?: () => any;
  onChange?: (currentData: Output) => any;
  readOnly?: boolean;
}

const initialState: State = {
  isLoaded: false,
  save: false,
  filteredProperties: [],
  propertiesSelected: [],
  isValidating: false,
  invoiceData: Invoice.create(),
  isSaving: false,
  error: undefined,
  fatalError: false,
  contractEvents: [],
  eventPage: 0,
  initiatedSchema: [],
  hasBeenChanged: false,
  isClosingWithoutSave: false,
  isAddingNewPM: false,
  selectedProperty: Property.create(),
  selectedEvent: Event.create(),
  createdEvent: Event.create(),
  isEditing: false,
  isDeleting: false,
  pendingSave: Contract.create(),
  sendInvoiceToCustomer: false,
  paymentProcessor: 'helcim',
  loadingEvents: false,
  sendInvoice: false,
  pendingSend: false,
  savingEvent: false,
  contractErrors: {},
};

const loadingContractEvents = Array.from({ length: 5 }, () => Event.create());

export const EditContractInfo: FC<props> = ({
  userID,
  contractID,
  onSaveFinished,
  onClose,
  onChange,
  readOnly = false,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [deletedJob, setDeletedJob] = useState<number | undefined>();
  const auth = useAuth();
  const {
    currentUserId,
    currentUserQuery,
    contractFrequenciesQuery,
    customerQuery,
    contractQuery,
    invoiceId,
    propertiesQuery,
  } = useEditContractQueries({ userID, contractID });

  const canDeleteJobs = useMemo(() => {
    return auth.user.permissionGroups.some((permission) => permission.name === 'CanDeleteJobs');
  }, [auth.user.permissionGroups]);

  useEffect(() => {
    if (!contractID) {
      dispatch({ type: ACTIONS.SET_PROPERTIES_SELECTED, data: [] });
    }
  }, [contractID]);

  const CONTRACT_SCHEMA: Schema<Contract> = useMemo(
    () => [
      [
        {
          label: 'Start Date',
          name: 'dateStarted',
          type: 'mui-date',
          required: true,
          disabled: readOnly,
        },
        {
          label: 'End Date',
          name: 'dateEnded',
          type: 'mui-date',
          required: true,
          disabled: readOnly,
        },
      ],
      [
        {
          label: 'Frequency',
          options:
            contractFrequenciesQuery.data?.results.map((item) => ({
              key: item.name + item.id,
              label: item.name,
              value: item.interval,
            })) || [],
          name: 'frequency',
          required: true,
          disabled: readOnly,
        },
        {
          label: 'Group Billing',
          options: GROUP_BILLING_OPTIONS.map((item) => ({
            key: item.name + item.value,
            label: item.name,
            value: item.value,
          })),
          name: 'groupBilling',
          required: true,
          disabled: readOnly,
        },
        {
          label: 'Payment Type',
          options: [...PAYMENT_TYPE_OPTIONS] satisfies Options,
          name: 'paymentType',
          required: true,
          disabled: readOnly,
        },
        {
          label: 'Payment Status',
          options: PAYMENT_STATUS_OPTIONS,
          name: 'paymentStatus',
          required: true,
          disabled: readOnly,
        },
      ],
      [
        {
          label: 'Department',
          name: 'departmentId',
          type: 'department',
          required: true,
          disabled: readOnly,
        },
        {},
      ],
      [
        {
          label: 'Service Start Time',
          name: 'timeStarted',
          type: 'mui-time',
          required: true,
          disabled: readOnly,
        },
        {
          label: 'Service End Time',
          name: 'timeEnded',
          type: 'mui-time',
          required: true,
          disabled: readOnly,
        },
        {},
      ],
      [
        {
          label: 'Notes',
          type: 'text',
          name: 'notes',
          multiline: true,
          minRows: 3,
          maxRows: 3,
          disabled: readOnly,
        },
        {
          label: 'Payment Terms',
          type: 'text',
          multiline: true,
          name: 'paymentTerms',
          minRows: 3,
          maxRows: 3,
          disabled: readOnly,
        },
      ],
    ],
    [contractFrequenciesQuery.data?.results, readOnly],
  );

  const [isContractJobsSectionShown, setIsContractJobsSectionShown] = useState(false);
  const contractEventsQuery = useEventBatchGetQuery({
    filters: {
      contractId: contractID,
      isActive: 1,
      pageNumber: state.eventPage,
      notEquals: ['PropertyId'],
      orderBy: 'date_started',
      orderDir: 'desc',
      withoutLimit: false,
    },
    select(data) {
      const updated = structuredClone(data) || EventList.create();
      updated.results = [...(data?.results || []).filter((item) => item.id !== deletedJob)];
      if ((data?.results || []).length > updated.results.length) {
        updated.totalCount = updated.totalCount - 1;
      }
      return updated;
    },
    enabled: isContractJobsSectionShown && contractID !== 0,
  });
  const contractAllEventsQuery = useEventBatchGetQuery({
    filters: {
      contractId: contractID,
      isActive: 1,
      pageNumber: state.eventPage,
      notEquals: ['PropertyId'],
      orderBy: 'date_started',
      orderDir: 'desc',
      withoutLimit: true,
      onlyEvents: true,
    },
    select(data) {
      const updated = structuredClone(data) || EventList.create();
      updated.results = [...(data?.results || []).filter((item) => item.id !== deletedJob)];
      if ((data?.results || []).length > updated.results.length) {
        updated.totalCount = updated.totalCount - 1;
      }
      return updated;
    },
    enabled: contractID !== 0,
  });

  const createVerboseContractLogging = useCallback(
    (entry: Contract) =>
      buildContractActivityMessage(
        entry,
        currentUserQuery.data?.firstname,
        currentUserQuery.data?.lastname,
      ),
    [currentUserQuery.data?.firstname, currentUserQuery.data?.lastname],
  );

  const handleDelete = async () => {
    const req = Event.create();
    req.id = state.selectedEvent.id;
    await EventClientService.Delete(req).then(async (res) => {
      if (res?.id) {
        setDeletedJob(res.id);

        // Create activity log
        const activityLog = ActivityLog.create();
        activityLog.activityName = `Deleted event: ${res.id}`;
        activityLog.activityDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
        activityLog.userId = currentUserId || 0;
        activityLog.eventId = res.id;
        await ActivityLogClientService.Create(activityLog);
      }
      queryClient.refetchQueries({
        queryKey: [queryKeys.events.root, queryKeys.events.list],
      });
    });
    dispatch({
      type: ACTIONS.SET_DELETING,
      data: { isDeleting: false, selectedEvent: Event.create() },
    });
  };

  const contractData = useMemo(() => {
    if (contractID === 0 || !contractQuery.data) {
      const data = buildNewContract(userID);
      dispatch({ type: ACTIONS.SET_PENDING_SAVE, data });
      return data;
    } else {
      dispatch({ type: ACTIONS.SET_PENDING_SAVE, data: contractQuery.data ?? Contract.create() });
      return contractQuery.data ?? Contract.create();
    }
  }, [contractID, contractQuery.data, userID]);

  useEffect(() => {
    if (propertiesQuery.isSuccess && contractData) {
      if (contractID !== 0) {
        const { selected: propertiesSelected, sorted: propertiesSorted } = orderContractProperties(
          contractData.properties,
          propertiesQuery.data.results,
        );
        dispatch({
          type: ACTIONS.SET_PROPERTIES_SELECTED,
          data: propertiesSelected,
        });
        dispatch({
          type: ACTIONS.SET_FILTERED_PROPERTIES,
          data: propertiesSorted,
        });
      } else {
        dispatch({
          type: ACTIONS.SET_FILTERED_PROPERTIES,
          data: propertiesQuery.data.results,
        });
      }

      dispatch({ type: ACTIONS.SET_LOADED, data: true });
    }
  }, [contractData, contractID, propertiesQuery.data?.results, propertiesQuery.isSuccess]);

  const saveContract = useCallback(async () => {
    const result = await saveContractRecord({
      pendingSave: state.pendingSave,
      propertiesSelected: state.propertiesSelected,
      contractID,
      currentUserId,
      activityMessage: createVerboseContractLogging,
    });

    if (!result.contract) {
      dispatch({
        type: ACTIONS.SET_ERROR,
        data: result.error,
      });
      return;
    }
    return result.contract;
  }, [
    state.pendingSave,
    state.propertiesSelected,
    contractID,
    createVerboseContractLogging,
    currentUserId,
  ]);

  const saveInvoice = useCallback(
    async (contract: Contract) =>
      saveContractInvoice({
        contract,
        invoiceData: state.invoiceData,
        invoiceId,
        customerLoaded: customerQuery.isSuccess,
        customerEmail: customerQuery.data?.email,
        sendInvoice: state.sendInvoice,
        sendInvoiceToCustomer: state.sendInvoiceToCustomer,
        paymentProcessor: state.paymentProcessor,
        userID,
        contractID,
      }),
    [
      customerQuery.isSuccess,
      customerQuery.data?.email,
      invoiceId,
      state.sendInvoice,
      state.invoiceData,
      state.sendInvoiceToCustomer,
      state.paymentProcessor,
      userID,
      contractID,
    ],
  );

  const save = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_SAVING, data: true });

    const contractResult = await saveContract();

    if (!contractResult) return;
    await saveInvoice(contractResult);

    // saveEvents();

    dispatch({ type: ACTIONS.SET_SAVING, data: false });
    if (onSaveFinished)
      onSaveFinished({
        contractData: contractData,
        propertiesSelected: state.propertiesSelected ? state.propertiesSelected : [],
        invoiceData: state.invoiceData,
      });
  }, [
    contractData,
    onSaveFinished,
    saveContract,
    saveInvoice,
    state.invoiceData,
    state.propertiesSelected,
  ]);

  const validateForSave = useCallback(() => {
    dispatch({ type: ACTIONS.SET_SAVE, data: false });
    if (state.propertiesSelected !== undefined) {
      if (state.propertiesSelected.length <= 0) {
        dispatch({ type: ACTIONS.SET_VALIDATING, data: true });
        return;
      }
    }
    save();
  }, [save, state.propertiesSelected]);

  const validateForClose = () => {
    if (state.hasBeenChanged) {
      dispatch({ type: ACTIONS.SET_CLOSING_WITHOUT_SAVE, data: true });
      return;
    }
    if (onClose) onClose();
  };

  const handlePropertyFilter = (data: any) => {
    dispatch({
      type: ACTIONS.SET_FILTERED_PROPERTIES,
      data: filterPropertiesByAddress(propertiesQuery.data?.results ?? [], data.target.value),
    });
  };

  const queryClient = useQueryClient();

  useEffect(() => {
    // if (!state.isLoaded) load();
    if (state.save) validateForSave();
  }, [state.isLoaded, state.save, validateForSave]);

  const contractEventsColumnDefs: ColumnDef<Event>[] = useMemo(
    () => [
      {
        accessorKey: 'id',
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="#" column={column} />
        ),
        cell({ row }) {
          return <div className="w-16">{row.original.id}</div>;
        },
      },
      {
        accessorKey: 'dateStarted',
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Start Date" column={column} />
        ),
        cell({ row }) {
          return <div className="w-32">{formatDate(row.original.dateStarted)}</div>;
        },
      },
      {
        id: 'address',
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Address" column={column} />
        ),
        cell({ row }) {
          const address = row.original.property?.address;
          return address ? <div className="w-32">{address}</div> : null;
        },
      },
      {
        id: 'typeAndSubtype',
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Job Type/SubType" column={column} />
        ),
        cell({ row }) {
          const event = row.original;
          return (
            <div className="w-32">{`${event.jobType ? event.jobType : '-'}/${
              event.jobSubtype ? event.jobSubtype : '-'
            }`}</div>
          );
        },
      },
      {
        accessorKey: 'logJobStatus',
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Job Status" column={column} />
        ),
        cell({ row }) {
          const value = row.getValue('logJobStatus');
          return <div className="w-32">{String(value)}</div>;
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Actions" column={column} />
        ),
        cell({ row }) {
          return (
            <div className="flex w-32 gap-2">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="px-2"
                      variant="outline"
                      onClick={() =>
                        dispatch({
                          type: ACTIONS.SET_EDITING,
                          data: { isEditing: true, selectedEvent: row.original },
                        })
                      }
                    >
                      <Pencil1Icon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="px-2 py-1">Edit service call</TooltipContent>
                </Tooltip>

                {canDeleteJobs && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="px-2"
                        variant="outline"
                        onClick={() =>
                          dispatch({
                            type: ACTIONS.SET_DELETING,
                            data: {
                              isDeleting: true,
                              selectedEvent: row.original,
                            },
                          })
                        }
                      >
                        <TrashIcon />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="px-2 py-1">Delete service call</TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>
          );
        },
      },
    ],
    [canDeleteJobs],
  );

  const skeletonColumns = useMemo<ColumnDef<Event>[]>(() => {
    return contractEventsColumnDefs.map((column) => ({
      ...column,
      cell: () => <Skeleton className="bg-foreground/30 h-4 w-32" />,
    }));
  }, [contractEventsColumnDefs]);

  const createEventMutation = useEventCreateMutation();
  const quickJobCreate = async (event: Event) => {
    dispatch({
      type: ACTIONS.SET_LOADED,
      data: false,
    });
    await createEventMutation.mutateAsync(event);

    dispatch({
      type: ACTIONS.SET_LOADED,
      data: true,
    });
  };
  const contractEventsTable = useReactTable({
    data: contractEventsQuery.isFetching
      ? loadingContractEvents
      : contractEventsQuery.data?.results ?? [],
    columns: contractEventsQuery.isFetching ? skeletonColumns : contractEventsColumnDefs,
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  const propertyColumns: ColumnDef<Property>[] = [
    {
      id: 'select',
      enableSorting: false,
      cell: ({ row }) => {
        const property = row.original;
        return (
          <Checkbox
            checked={state.propertiesSelected?.some((p) => p.id === property.id) ?? false}
            name={property.address}
            disabled={readOnly}
            onClick={() => {
              if (readOnly) return;
              dispatch({
                type: ACTIONS.SET_PROPERTIES_SELECTED,
                data: toggleSelectedProperty(state.propertiesSelected, property),
              });
            }}
          />
        );
      },
    },
    {
      accessorKey: 'address',
      header: ({ column }) => <DataTableColumnHeader title="Address" column={column} />,
      cell: ({ row }) => <Typography sx={{ textAlign: 'left' }}>{row.original.address}</Typography>,
    },
    {
      accessorKey: 'city',
      header: ({ column }) => <DataTableColumnHeader title="City" column={column} />,
      cell: ({ row }) => <Typography sx={{ textAlign: 'left' }}>{row.original.city}</Typography>,
    },
    {
      id: 'pmCount',
      enableSorting: false,
      cell: ({ row }) => {
        const property = row.original;
        const displayPMCount = isPropertyOnContract(contractData, property);
        if (!displayPMCount) return null;

        const pmCount =
          contractAllEventsQuery.data?.results.filter((event) => event.propertyId === property.id)
            .length ?? 0;

        return <Typography sx={{ textAlign: 'right' }}>{`PM's ${pmCount}`}</Typography>;
      },
    },
    {
      id: 'quickCreate',
      enableSorting: false,
      cell: ({ row }) => {
        const property = row.original;
        const displayPMCount = isPropertyOnContract(contractData, property);
        if (!displayPMCount) return null;

        return (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger>
                <IconButton
                  disabled={readOnly}
                  onClick={() => {
                    if (readOnly) return;
                    const event = buildContractPmEvent(property, contractData, 'quick');
                    quickJobCreate(event);
                  }}
                >
                  <AddCircleOutlineTwoTone sx={{ fontSize: 20 }} />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>Quick create job</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      id: 'edit',
      enableSorting: false,
      cell: ({ row }) => {
        const property = row.original;
        const displayPMCount = isPropertyOnContract(contractData, property);
        if (!displayPMCount) return null;

        return (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger>
                <IconButton
                  disabled={readOnly}
                  onClick={() => {
                    if (readOnly) return;
                    const event = buildContractPmEvent(property, contractData, 'request');
                    dispatch({
                      type: ACTIONS.SET_ADDING_NEW_PM,
                      data: {
                        isAdding: true,
                        selectedProperty: property,
                        selectedEvent: event,
                      },
                    });
                  }}
                >
                  <EditSharp sx={{ fontSize: 20 }} />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>Edit property</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
  ];

  const propertyTable = useReactTable({
    data: state.filteredProperties,
    columns: propertyColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  return (
    <>
      {state.isSaving || !state.isLoaded ? <Loader /> : undefined}
      <Modal
        open={state.pendingSend}
        onClose={() => dispatch({ type: ACTIONS.SET_PENDING_SEND, data: false })}
      >
        <Confirm
          title={'Send Invoice to Customer'}
          open={state.pendingSend}
          disabled={state.isSaving}
          submitLabel={'Save'}
          cancelLabel={'Cancel'}
          onConfirm={() => {
            dispatch({ type: ACTIONS.SET_SEND_INVOICE, data: true });
            if (!onClose) dispatch({ type: ACTIONS.SET_PENDING_SEND, data: false });
            dispatch({ type: ACTIONS.SET_SAVE, data: true });
          }}
          onClose={() => {
            dispatch({ type: ACTIONS.SET_PENDING_SEND, data: false });
          }}
        >
          <div className="flex flex-col gap-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.sendInvoiceToCustomer}
                  disabled={state.isSaving}
                  onChange={() =>
                    dispatch({
                      type: ACTIONS.SET_SEND_INVOICE_TO_CUSTOMER,
                      data: !state.sendInvoiceToCustomer,
                    })
                  }
                />
              }
              label={`Send Invoice To Customer?`}
            />
            {state.sendInvoiceToCustomer && (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Payment Processor
                </label>
                <Select
                  value={state.paymentProcessor}
                  onValueChange={(value: 'helcim' | 'stripe') =>
                    dispatch({ type: ACTIONS.SET_PAYMENT_PROCESSOR, data: value })
                  }
                  disabled={state.isSaving}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="helcim" className="bg-white hover:bg-gray-50">
                      Helcim
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-gray-500">
                  Select which payment processor to use for the payment link
                </p>
              </div>
            )}
          </div>
        </Confirm>
      </Modal>
      <Modal
        open={state.isAddingNewPM}
        onClose={() => {
          dispatch({
            type: ACTIONS.SET_ADDING_NEW_PM,
            data: {
              isAdding: false,
              selectedProperty: Property.create(),
              selectedEvent: Event.create(),
            },
          });
        }}
      >
        <ServiceRequest
          propertyId={state.selectedProperty.id}
          serviceCallId={0}
          serviceEvent={state.selectedEvent}
          onSave={() => {
            queryClient.invalidateQueries({
              queryKey: [queryKeys.contracts.root],
            });
            queryClient.invalidateQueries({
              queryKey: [queryKeys.events.root],
            });
            dispatch({
              type: ACTIONS.SET_ADDING_NEW_PM,
              data: {
                isAdding: false,
                selectedProperty: Property.create(),
                selectedEvent: Event.create(),
              },
            });
          }}
          onClose={() => {
            contractAllEventsQuery.refetch();
            dispatch({
              type: ACTIONS.SET_ADDING_NEW_PM,
              data: {
                isAdding: false,
                selectedProperty: Property.create(),
                selectedEvent: Event.create(),
              },
            });
          }}
        />
      </Modal>
      <Modal
        open={state.isEditing}
        onClose={() =>
          dispatch({
            type: ACTIONS.SET_EDITING,
            data: { isEditing: false, selectedEvent: Event.create() },
          })
        }
        fullScreen
      >
        <ServiceCall
          propertyId={state.selectedEvent.propertyId}
          serviceCallId={state.selectedEvent.id}
          onSave={() => {
            queryClient.invalidateQueries({
              queryKey: [queryKeys.events.root],
            });
            dispatch({
              type: ACTIONS.SET_EDITING,
              data: { isEditing: false, selectedEvent: Event.create() },
            });
          }}
          onClose={() =>
            dispatch({
              type: ACTIONS.SET_EDITING,
              data: { isEditing: false, selectedEvent: Event.create() },
            })
          }
        />
      </Modal>
      {state.isDeleting && (
        <Confirm
          title={`Delete Job ${state.selectedEvent.id}`}
          open={state.isDeleting}
          onClose={() => {
            dispatch({
              type: ACTIONS.SET_DELETING,
              data: { isDeleting: false, selectedEvent: Event.create() },
            });
          }}
          onConfirm={handleDelete}
        >
          Are you sure you want to delete this job?
        </Confirm>
      )}
      {state.error && (
        <Alert
          title={ERROR_LABEL}
          open={state.error !== undefined}
          onClose={() => {
            dispatch({ type: ACTIONS.SET_ERROR, data: undefined });
          }}
        >
          {state.error}
        </Alert>
      )}
      {state.isClosingWithoutSave && (
        <Confirm
          title={WARNING_LABEL}
          open={state.isClosingWithoutSave}
          onClose={() => {
            dispatch({ type: ACTIONS.SET_CLOSING_WITHOUT_SAVE, data: false });
          }}
          onConfirm={() => {
            dispatch({ type: ACTIONS.SET_CLOSING_WITHOUT_SAVE, data: false });
            if (onClose) onClose();
          }}
        >
          {CONFIRM_CLOSE_WITHOUT_SAVE_DIALOG}
        </Confirm>
      )}
      {state.isValidating && (
        <Confirm
          title={CONFIRM_SAVE_LABEL}
          open={true}
          onClose={() => dispatch({ type: ACTIONS.SET_VALIDATING, data: false })}
          onConfirm={() => {
            save();
            dispatch({ type: ACTIONS.SET_VALIDATING, data: false });
          }}
        >
          {CONFIRM_SAVE_DIALOG}
        </Confirm>
      )}
      <SectionBar
        title={`Customer: ${customerQuery.data?.firstname || ''} ${
          customerQuery.data?.lastname || ''
        }`}
        actions={[
          ...(readOnly
            ? []
            : [
                {
                  label: contractID ? SAVE_LABEL : 'Create',
                  disabled: !state.propertiesSelected?.length,
                  onClick: () => {
                    const errors: ContractErrors = checkContractErrors(state.pendingSave);
                    dispatch({
                      type: ACTIONS.SET_CONTRACT_ERRORS,
                      data: errors,
                    });
                    if (!Object.keys(errors).length) {
                      dispatch({ type: ACTIONS.SET_PENDING_SEND, data: true });
                    }
                  },
                },
              ]),
          { label: CANCEL_LABEL, onClick: () => validateForClose() },
        ]}
        sticky
        styles={{ zIndex: 5 }}
      >
        {!state.propertiesSelected?.length && (
          <div style={{ color: 'red', margin: '10px 0', textAlign: 'center' }}>
            Please select a property to proceed.
          </div>
        )}
        {state.isLoaded && (
          <Grid container spacing={0}>
            <Grid item xs={12} md={8}>
              <SectionBar
                title={contractID > 0 ? `Edit Contract: ${contractData.number}` : 'New Contract'}
                uncollapsable
              >
                <div
                  style={{
                    height: '435px',
                    overflow: 'scroll',
                    borderRight: 1,
                    borderRightStyle: 'solid',
                    borderRightWidth: '15px',
                    borderRightColor: 'lightgray',
                  }}
                >
                  <PlainForm<Contract>
                    error={state.fatalError}
                    key={JSON.stringify(contractData)}
                    schema={CONTRACT_SCHEMA}
                    data={contractData}
                    checkForDataPropChanges={true}
                    className="EditContractInfo"
                    onSubmit={validateForSave}
                    attachFieldMask={true}
                    // onClose={() => onClose()}
                    onChange={(contractData) => {
                      const errors: ContractErrors = checkContractChange(
                        contractData,
                        state.pendingSave,
                        state.contractErrors,
                      );
                      dispatch({
                        type: ACTIONS.SET_CONTRACT_ERRORS,
                        data: errors,
                      });
                      dispatch({
                        type: ACTIONS.SET_HAS_BEEN_CHANGED,
                        data: true,
                      });
                      const req = applyContractFormChange(contractData, state.pendingSave, userID);

                      dispatch({
                        type: ACTIONS.SET_PENDING_SAVE,
                        data: req,
                      });
                      if (onChange)
                        onChange({
                          contractData: req,
                          propertiesSelected: state.propertiesSelected,
                          invoiceData: state.invoiceData,
                        } as Output);
                    }}
                    validations={state.contractErrors}
                  />
                </div>
              </SectionBar>
            </Grid>
            <Grid item xs={12} md={4}>
              <SectionBar title={'Properties'} uncollapsable>
                <FormControl
                  component="fieldset"
                  variant="outlined"
                  fullWidth
                  sx={{ paddingBottom: '20px', paddingLeft: '5px', paddingTop: '16px' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '20px',
                      paddingTop: '8px',
                      paddingBottom: '12px',
                      paddingLeft: '4px',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: '#666',
                        fontWeight: 500,
                      }}
                    >
                      Number of Selected Properties:
                    </Typography>
                    <Badge
                      variant="secondary"
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        padding: '4px 12px',
                      }}
                    >
                      {state.propertiesSelected?.length || 0}
                    </Badge>
                  </div>
                  <div>
                    <TextField
                      label="Address"
                      variant="standard"
                      sx={{ width: '98%', alignContent: 'center' }}
                      onChange={debounce(handlePropertyFilter, 300)}
                      disabled={readOnly}
                    />
                  </div>
                  <div style={{ height: '365px', overflowY: 'auto' }}>
                    <DataTable table={propertyTable} />
                  </div>
                </FormControl>
              </SectionBar>
            </Grid>
          </Grid>
        )}
      </SectionBar>
      {state.isLoaded && !contractQuery.isFetching && (
        <SectionBar
          title={INVOICE_SECTION_NAME}
          uncollapsable
          // actions={[{ label: CANCEL_LABEL, onClick: () => validateForClose() }]}
        >
          <EditInvoiceData
            userId={userID}
            contract={contractData}
            onClose={() => onClose?.()}
            onSave={(savedInvoice) => {
              dispatch({
                type: ACTIONS.SET_INVOICE_DATA,
                data: savedInvoice,
              });
              validateForSave();
            }}
            onLoad={(loadedData) => {
              dispatch({
                type: ACTIONS.SET_INVOICE_DATA,
                data: loadedData,
              });
              if (onChange)
                onChange({
                  contractData: contractData,
                  propertiesSelected:
                    state.propertiesSelected !== undefined ? state.propertiesSelected : [],
                  invoiceData: state.invoiceData,
                });
            }}
            onChange={(currentData) => {
              dispatch({ type: ACTIONS.SET_HAS_BEEN_CHANGED, data: true });
              dispatch({
                type: ACTIONS.SET_INVOICE_DATA,
                data: currentData,
              });
              if (onChange)
                onChange({
                  contractData: contractData,
                  propertiesSelected:
                    state.propertiesSelected !== undefined ? state.propertiesSelected : [],
                  invoiceData: state.invoiceData,
                });
            }}
            readOnly={readOnly}
          />
        </SectionBar>
      )}
      {state.isLoaded && contractID !== 0 && (
        <SectionBar
          title={'Service Calls'}
          sticky={false}
          subtitle={SERVICE_CALL_SUBTITLE}
          pagination={{
            count: contractEventsQuery.data?.totalCount ?? 0,
            rowsPerPage: 25,
            page: state.eventPage,
            onPageChange: (page) => {
              dispatch({ type: ACTIONS.SET_EVENT_PAGE, data: page });
            },
          }}
          uncollapsable
          defaultCollapsed={!isContractJobsSectionShown}
          onToggleCollapse={() => {
            setIsContractJobsSectionShown((value) => !value);
          }}
        >
          {state.loadingEvents && <Loader />}
          <DataTable table={contractEventsTable} />
        </SectionBar>
      )}
    </>
  );
};
